import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TEAMS, REQUEST_TYPES } from "@/lib/crmConstants";
import { LogOut, LayoutDashboard, Users, Settings, FileText, Download, Activity, ExternalLink, Plus, Map, Store, Coins, Barcode, ShieldCheck, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

const teamAllowedTypes = (user) => {
  if (!user) return [];
  if (user.role === "admin") return Object.keys(REQUEST_TYPES);
  if (user.role === "sales_agent") return [];
  if (user.role === "sales_incentive") return [];
  const map = {
    sales: ["contact_us_lead", "contact_us_general", "contact_us_feedback"],
    service: ["contact_us_complaint"],
    bulk_procurement: ["bulk_procurement"],
    dealership_bd: ["dealership"],
    csr: ["csr"],
    warranty: ["warranty"],
  };
  const s = new Set();
  (user.teams || []).forEach((t) => (map[t] || []).forEach((x) => s.add(x)));
  return Array.from(s);
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const types = teamAllowedTypes(user);
  const isAgent = user?.role === "sales_agent";
  const isIncentiveRole = user?.role === "sales_incentive";
  const isWarrantyTeam = (user?.teams || []).includes("warranty");
  const restrictedRole = isAgent || isIncentiveRole;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "bg-white text-[#4F46E5] border border-[#E5E5E5] shadow-sm"
        : "text-[#3F3F46] hover:bg-white/70"
    }`;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--page-bg)" }}>
      <aside
        className="w-64 flex-shrink-0 border-r"
        style={{ backgroundColor: "var(--sidebar-bg)", borderColor: "var(--border)" }}
      >
        <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#4F46E5] flex items-center justify-center">
              <span className="text-white font-heading font-black text-lg">C</span>
            </div>
            <div>
              <div className="font-heading font-black tracking-tight text-[15px] leading-tight">Hero Eco Med CRM</div>
              <div className="data-label mt-0.5" style={{ fontSize: "0.6rem" }}>Sales · Service · Ops</div>
            </div>
          </Link>
        </div>

        <nav className="p-3 space-y-1">
          <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          {!restrictedRole && (
            <NavLink to="/records" className={linkCls} data-testid="nav-records">
              <FileText className="w-4 h-4" /> All Records
            </NavLink>
          )}
          {isIncentiveRole && (
            <>
              <NavLink to="/incentive-approvals" className={linkCls} data-testid="nav-incentive-approvals">
                <ShieldCheck className="w-4 h-4" /> Approvals
              </NavLink>
              <NavLink to="/unique-ids" className={linkCls} data-testid="nav-unique-ids-incentive">
                <Barcode className="w-4 h-4" /> Unique IDs
              </NavLink>
              <NavLink to="/payouts" className={linkCls} data-testid="nav-payouts-incentive">
                <Wallet className="w-4 h-4" /> Payouts
              </NavLink>
            </>
          )}
          {isWarrantyTeam && user?.role !== "admin" && (
            <NavLink to="/warranty-approvals" className={linkCls} data-testid="nav-warranty-approvals">
              <ShieldCheck className="w-4 h-4" /> Warranty Approvals
            </NavLink>
          )}

          {!restrictedRole && types.length > 0 && (
            <>
              <div className="pt-4 pb-1 px-3 data-label">Request Types</div>
              {types.map((t) => (
                <NavLink
                  key={t}
                  to={`/records?type=${t}`}
                  className={linkCls}
                  data-testid={`nav-type-${t}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                  <span className="truncate">{REQUEST_TYPES[t].label}</span>
                </NavLink>
              ))}
            </>
          )}

          {!restrictedRole && (
            <>
              <div className="pt-4 pb-1 px-3 data-label">Actions</div>
              <NavLink to="/new" className={linkCls} data-testid="nav-new-record">
                <Plus className="w-4 h-4" /> Manual Entry
              </NavLink>
              <NavLink to="/handoff" className={linkCls} data-testid="nav-handoff">
                <ExternalLink className="w-4 h-4" /> Website Forms Handoff
              </NavLink>
            </>
          )}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 pb-1 px-3 data-label">Admin</div>
              <NavLink to="/users" className={linkCls} data-testid="nav-users">
                <Users className="w-4 h-4" /> Users &amp; Teams
              </NavLink>
              <NavLink to="/territories" className={linkCls} data-testid="nav-territories">
                <Map className="w-4 h-4" /> Territories
              </NavLink>
              <NavLink to="/stores" className={linkCls} data-testid="nav-stores">
                <Store className="w-4 h-4" /> Stores
              </NavLink>
              <NavLink to="/incentives" className={linkCls} data-testid="nav-incentives">
                <Coins className="w-4 h-4" /> Incentives
              </NavLink>
              <NavLink to="/unique-ids" className={linkCls} data-testid="nav-unique-ids">
                <Barcode className="w-4 h-4" /> Unique IDs
              </NavLink>
              <NavLink to="/incentive-approvals" className={linkCls} data-testid="nav-incentive-approvals-admin">
                <ShieldCheck className="w-4 h-4" /> Incentive Approvals
              </NavLink>
              <NavLink to="/warranty-approvals" className={linkCls} data-testid="nav-warranty-approvals-admin">
                <ShieldCheck className="w-4 h-4" /> Warranty Approvals
              </NavLink>
              <NavLink to="/payouts" className={linkCls} data-testid="nav-payouts-admin">
                <Wallet className="w-4 h-4" /> Payouts
              </NavLink>
              <NavLink to="/audit" className={linkCls} data-testid="nav-audit">
                <Activity className="w-4 h-4" /> Audit Log
              </NavLink>
              <NavLink to="/settings" className={linkCls} data-testid="nav-settings">
                <Settings className="w-4 h-4" /> Integrations
              </NavLink>
            </>
          )}
        </nav>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header
          className="h-14 border-b flex items-center justify-between px-6"
          style={{ backgroundColor: "#FFFFFF", borderColor: "var(--border)" }}
        >
          <div className="flex items-center gap-3">
            <div className="data-label">Signed in as</div>
            <div className="text-sm">
              <span className="font-medium">{user?.name}</span>
              <span className="text-[#71717A]"> · {user?.email}</span>
            </div>
            <span
              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border font-mono"
              style={{
                backgroundColor: user?.role === "admin" ? "#EEF2FF" : user?.role === "sales_agent" ? "#DCFCE7" : user?.role === "sales_incentive" ? "#FEF3C7" : "#F4F4F5",
                color: user?.role === "admin" ? "#4338CA" : user?.role === "sales_agent" ? "#166534" : user?.role === "sales_incentive" ? "#92400E" : "#3F3F46",
                borderColor: user?.role === "admin" ? "#C7D2FE" : user?.role === "sales_agent" ? "#86EFAC" : user?.role === "sales_incentive" ? "#FCD34D" : "#E4E4E7",
              }}
              data-testid="user-role-chip"
            >
              {user?.role === "admin"
                ? "Administrator"
                : user?.role === "sales_agent"
                ? "Sales Agent"
                : user?.role === "sales_incentive"
                ? "Sales Incentives"
                : (user?.teams || []).join(", ") || "team_member"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!restrictedRole && (
              <Button
                variant="outline"
                size="sm"
                data-testid="topbar-export-csv"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export.csv`, "_blank")}
              >
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            )}
            {user?.role === "admin" && (
              <Button
                variant="outline"
                size="sm"
                data-testid="topbar-export-full-db"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export_full.csv`, "_blank")}
              >
                <Download className="w-4 h-4 mr-1" /> Full DB
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </header>
        <div className="flex-1 overflow-auto">{children}</div>
      </main>
    </div>
  );
}
